import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, DollarSign, TrendingUp, Lock, Clock, Send, Ban,
  CheckCircle2, XCircle, AlertTriangle, CreditCard, Bitcoin,
  Calendar, Filter, ArrowUpRight, ArrowDownRight, Edit
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  en_attente: { label: 'En attente d\'envoi', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  envoye: { label: 'Envoyé', color: 'bg-blue-500/20 text-blue-400', icon: Send },
  bloque: { label: 'Bloqué', color: 'bg-red-500/20 text-red-400', icon: Ban },
  recu: { label: 'Reçu', color: 'bg-green-500/20 text-green-400', icon: CheckCircle2 },
  paye: { label: 'Payé', color: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle2 },
  annule: { label: 'Annulé', color: 'bg-gray-500/20 text-gray-400', icon: XCircle },
};

export default function AnalystPayments() {
  const { token, user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [analystProfile, setAnalystProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filters
  const now = new Date();
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}`;
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(lastOfMonth);
  const [filterStatus, setFilterStatus] = useState('all');

  // New payment form
  const [form, setForm] = useState({
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    type: 'virement',
    rib_label: '',
    status: 'en_attente',
  });

  useEffect(() => {
    fetchData();
  }, [token, startDate, endDate]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const [paymentsRes, clientsRes, statsRes, profileRes] = await Promise.all([
        axios.get(`${API}/payments?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payments/stats?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users/${user?.id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setPayments(paymentsRes.data);
      setClients(clientsRes.data || []);
      setStats(statsRes.data);
      if (profileRes.data) setAnalystProfile(profileRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const c = clients.find(cl => cl.user_id === clientId || cl.id === clientId);
    if (c) {
      const u = c.user || c;
      return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || clientId;
    }
    return clientId;
  };

  const handleCreate = async () => {
    if (!form.client_id || !form.amount || !form.date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/payments`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Paiement créé avec succès');
      setDialogOpen(false);
      setForm({ client_id: '', date: new Date().toISOString().split('T')[0], amount: '', type: 'virement', rib_label: '', status: 'en_attente' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPayment) return;
    setSaving(true);
    try {
      const updateData = {
        date: selectedPayment.date,
        amount: selectedPayment.amount,
        type: selectedPayment.type,
        rib_label: selectedPayment.rib_label || '',
        status: selectedPayment.status,
      };
      await axios.put(`${API}/payments/${selectedPayment.id}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Paiement mis à jour');
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (payment) => {
    setSelectedPayment({ ...payment });
    setEditDialogOpen(true);
  };

  const filteredPayments = filterStatus === 'all' ? payments : payments.filter(p => p.status === filterStatus);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analyst-payments-page">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Mes Paiements</h1>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-payment-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Paiement
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Paiements</p>
                      <p className="text-2xl font-bold">{stats.total_payments}</p>
                      <p className="text-sm text-muted-foreground">{stats.total_amount?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-primary opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Commissions à venir</p>
                      <p className="text-2xl font-bold text-yellow-400">{stats.commission_pending?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Commissions confirmées</p>
                      <p className="text-2xl font-bold text-emerald-400">{stats.commission_confirmed?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <CheckCircle2 className="w-8 h-8 text-emerald-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Commissions perdues</p>
                      <p className="text-2xl font-bold text-red-400">{stats.commission_lost?.toLocaleString('fr-FR')} €</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-400 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-400" /> Virements
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Nombre</span>
                      <span className="font-semibold">{stats.count_virement || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Montant total</span>
                      <span className="font-semibold">{stats.total_virement?.toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Commission à venir</span>
                      <span className="font-semibold text-yellow-400">{stats.commission_virement_pending?.toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Commission confirmée</span>
                      <span className="font-semibold text-emerald-400">{stats.commission_virement_confirmed?.toLocaleString('fr-FR')} €</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Bitcoin className="w-4 h-4 text-amber-400" /> Crypto
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Nombre</span>
                      <span className="font-semibold">{stats.count_crypto || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Montant total</span>
                      <span className="font-semibold">{stats.total_crypto?.toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Commission à venir</span>
                      <span className="font-semibold text-yellow-400">{stats.commission_crypto_pending?.toLocaleString('fr-FR')} €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Commission confirmée</span>
                      <span className="font-semibold text-emerald-400">{stats.commission_crypto_confirmed?.toLocaleString('fr-FR')} €</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Breakdown */}
            {stats.by_status && Object.keys(stats.by_status).length > 0 && (
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wider mb-3">Répartition par statut</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(stats.by_status).map(([status, count]) => {
                      const sc = STATUS_CONFIG[status] || {};
                      return (
                        <div key={status} className="flex items-center gap-2">
                          <Badge className={`${sc.color || ''} text-xs`}>{sc.label || status}</Badge>
                          <span className="font-semibold text-sm">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Date Filters */}
        <Card className="bg-card/80 backdrop-blur border-border/50">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Du</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-44" data-testid="filter-start-date" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Au</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-44" data-testid="filter-end-date" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Statut</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48" data-testid="filter-status">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="en_attente">En attente d'envoi</SelectItem>
                    <SelectItem value="envoye">Envoyé</SelectItem>
                    <SelectItem value="bloque">Bloqué</SelectItem>
                    <SelectItem value="recu">Reçu</SelectItem>
                    <SelectItem value="paye">Payé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData} data-testid="filter-apply-btn">
                <Filter className="w-4 h-4 mr-1" /> Filtrer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card className="bg-card/80 backdrop-blur border-border/50">
          <CardHeader>
            <CardTitle className="text-base uppercase tracking-wider">Liste des paiements</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun paiement trouvé pour cette période</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Date</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Client</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Montant</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Type</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Intitulé RIB</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Statut</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Commission</th>
                      <th className="text-right py-3 px-2 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map(p => {
                      const sc = STATUS_CONFIG[p.status] || {};
                      const Icon = sc.icon || Clock;
                      const commRate = p.type === 'crypto' ? (analystProfile?.commission_crypto || 0) : (analystProfile?.commission_virement || 0);
                      const commission = (p.amount || 0) * commRate / 100;
                      const isLocked = p.locked;
                      return (
                        <tr key={p.id} className={`border-b border-border/20 hover:bg-secondary/20 ${isLocked ? 'opacity-75' : ''}`} data-testid={`payment-row-${p.id}`}>
                          <td className="py-3 px-2">{new Date(p.date).toLocaleDateString('fr-FR')}</td>
                          <td className="py-3 px-2">{getClientName(p.client_id)}</td>
                          <td className="py-3 px-2 font-semibold">{p.amount?.toLocaleString('fr-FR')} €</td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="text-xs">
                              {p.type === 'crypto' ? <><Bitcoin className="w-3 h-3 mr-1" />Crypto</> : <><CreditCard className="w-3 h-3 mr-1" />Virement</>}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-muted-foreground">{p.type === 'virement' ? (p.rib_label || '-') : '-'}</td>
                          <td className="py-3 px-2">
                            <Badge className={`${sc.color} text-xs`}>
                              <Icon className="w-3 h-3 mr-1" />
                              {sc.label}
                              {isLocked && <Lock className="w-3 h-3 ml-1" />}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            {['bloque', 'annule'].includes(p.status) ? (
                              <span className="text-red-400 line-through">{commission.toLocaleString('fr-FR')} €</span>
                            ) : p.status === 'paye' ? (
                              <span className="text-emerald-400 font-semibold">{commission.toLocaleString('fr-FR')} €</span>
                            ) : (
                              <span className="text-yellow-400">{commission.toLocaleString('fr-FR')} €</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-right">
                            {!isLocked && (
                              <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-payment-${p.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Payment Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="create-payment-dialog">
            <DialogHeader>
              <DialogTitle>Nouveau Paiement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={v => setForm(prev => ({ ...prev, client_id: v }))}>
                  <SelectTrigger data-testid="payment-client-select">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.user_id || c.id} value={c.user_id || c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))} data-testid="payment-date-input" />
              </div>
              <div>
                <Label>Montant (€) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(prev => ({ ...prev, amount: e.target.value }))} placeholder="0.00" data-testid="payment-amount-input" />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={v => setForm(prev => ({ ...prev, type: v, rib_label: v === 'crypto' ? '' : prev.rib_label }))}>
                  <SelectTrigger data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="virement">Virement</SelectItem>
                    <SelectItem value="crypto">Crypto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.type === 'virement' && (
                <div>
                  <Label>Intitulé du RIB</Label>
                  <Input value={form.rib_label} onChange={e => setForm(prev => ({ ...prev, rib_label: e.target.value }))} placeholder="Ex: Banque Populaire - FR76..." data-testid="payment-rib-input" />
                </div>
              )}
              <div>
                <Label>Statut</Label>
                <Select value={form.status} onValueChange={v => setForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="payment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_attente">En attente d'envoi</SelectItem>
                    <SelectItem value="envoye">Envoyé</SelectItem>
                    <SelectItem value="bloque">Bloqué</SelectItem>
                    <SelectItem value="recu">Reçu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="submit-payment-btn">
                {saving ? 'Création...' : 'Créer le paiement'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Payment Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
            <DialogHeader>
              <DialogTitle>Modifier le Paiement</DialogTitle>
            </DialogHeader>
            {selectedPayment && (
              <div className="space-y-4">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={selectedPayment.date} onChange={e => setSelectedPayment(prev => ({ ...prev, date: e.target.value }))} />
                </div>
                <div>
                  <Label>Montant (€)</Label>
                  <Input type="number" step="0.01" value={selectedPayment.amount} onChange={e => setSelectedPayment(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={selectedPayment.type} onValueChange={v => setSelectedPayment(prev => ({ ...prev, type: v, rib_label: v === 'crypto' ? '' : prev.rib_label }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="virement">Virement</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {selectedPayment.type === 'virement' && (
                  <div>
                    <Label>Intitulé du RIB</Label>
                    <Input value={selectedPayment.rib_label || ''} onChange={e => setSelectedPayment(prev => ({ ...prev, rib_label: e.target.value }))} />
                  </div>
                )}
                <div>
                  <Label>Statut</Label>
                  <Select value={selectedPayment.status} onValueChange={v => setSelectedPayment(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en_attente">En attente d'envoi</SelectItem>
                      <SelectItem value="envoye">Envoyé</SelectItem>
                      <SelectItem value="bloque">Bloqué</SelectItem>
                      <SelectItem value="recu">Reçu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdate} disabled={saving} data-testid="update-payment-btn">
                {saving ? 'Mise à jour...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
